import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Progress,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCircleCheck,
  IconCircleX,
  IconLoader,
  IconPlayerStop,
} from "@tabler/icons-react";
import {
  isActiveStatus,
  toValidationJobStatus,
  toValidationStage,
  useCancelValidation,
  useValidationProgress,
  VALIDATION_STAGE_LABELS,
  VALIDATION_STATUS_COLORS,
} from "./validation.service";
import { ValidationInvariantChecklist } from "./ValidationInvariantChecklist";
import { ValidationErrorList } from "./ValidationErrorCard";
import { ValidationEventFeed } from "./ValidationEventFeed";
import { ValidationStructuredErrorList } from "./ValidationStructuredErrorList";

interface RuleSetValidationPanelProps {
  readonly jobId: string | null;
}

function RuleSetValidationPanel(props: RuleSetValidationPanelProps) {
  const { jobId } = props;
  const { data, loading } = useValidationProgress(jobId);
  const [cancel, { loading: cancelling }] = useCancelValidation();

  if (!jobId) return null;

  const job = data?.validationProgress;

  if (!job) {
    return (
      <Card shadow="sm" padding="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <IconLoader size={16} />
            <Text size="sm">Validation queued, waiting for updates…</Text>
          </Group>
          {loading ? <Skeleton height={8} radius="sm" /> : null}
        </Stack>
      </Card>
    );
  }

  const progress = job.progress;
  const percent: number = progress?.percent ?? 0;
  const stage = toValidationStage(job.stage);
  const status = toValidationJobStatus(job.status);
  const detail: string | null = progress?.currentPhaseDetail ?? null;
  const rulesFired: number = progress?.rulesFired ?? 0;
  const totalRules: number = progress?.totalRulesDeclared ?? 0;
  const elapsedMs: number = progress?.elapsedMs ?? 0;
  const stageLabel = VALIDATION_STAGE_LABELS[stage];

  const active = isActiveStatus(status);

  async function handleCancel(): Promise<void> {
    if (!jobId) return;
    try {
      const result = await cancel({ variables: { jobId } });
      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (err) {
      notifications.show({
        title: "Failed to cancel validation",
        message: err instanceof Error ? err.message : "Unknown error",
        color: "red",
      });
    }
  }

  return (
    <Card shadow="sm" padding="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>Rule Set Validation</Text>
          <Group gap="xs">
            {active && (
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconPlayerStop size={14} />}
                loading={cancelling}
                onClick={() => void handleCancel()}
              >
                Cancel
              </Button>
            )}
            <Badge color={VALIDATION_STATUS_COLORS[status]} variant="light">
              {status}
            </Badge>
          </Group>
        </Group>

        {active && (
          <>
            <Progress value={percent} animated size="sm" />
            <Group gap="xs" justify="space-between">
              <Text size="sm" c="dimmed">
                {stageLabel}
                {detail ? ` · ${detail}` : ""}
              </Text>
              <Text size="xs" c="dimmed">
                {elapsedMs > 0 ? `${(elapsedMs / 1000).toFixed(1)}s` : ""}
              </Text>
            </Group>
            {totalRules > 0 && (
              <Text size="xs" c="dimmed">
                Rules fired: {rulesFired} / {totalRules}
              </Text>
            )}
          </>
        )}

        {status === "FAILED" && job.errorSummary ? (
          <Alert
            color="red"
            icon={<IconCircleX size={16} />}
            title="Validation failed"
          >
            {job.errorSummary}
          </Alert>
        ) : null}

        {status === "CANCELLED" ? (
          <Alert color="gray" title="Cancelled">
            Validation was cancelled.
          </Alert>
        ) : null}

        {status === "SUCCEEDED" ? (
          <Alert
            color="green"
            icon={<IconCircleCheck size={16} />}
            title="Validated"
          >
            Rule set passed validation. The step is ready for generation.
          </Alert>
        ) : null}

        {status === "SUCCEEDED" || status === "FAILED" ? (
          <>
            <Divider />
            <ValidationInvariantChecklist jobId={jobId} />
            {status === "FAILED" ? (
              <>
                <ValidationStructuredErrorList jobId={jobId} />
                <ValidationErrorList jobId={jobId} />
              </>
            ) : null}
          </>
        ) : null}

        <Divider />
        <ValidationEventFeed jobId={jobId} />
      </Stack>
    </Card>
  );
}

export { RuleSetValidationPanel };
export type { RuleSetValidationPanelProps };
